import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { CheckCircle, ArrowsClockwise, Sparkle, PencilSimple } from "@phosphor-icons/react";

export default function SalaryReconcile() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);   // { line, allocations, source_ledger_id }

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/salary-reconcile/unmatched");
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const applyMatch = async (line, allocations) => {
        try {
            await api.post(`/bank-lines/${line.id}/apply-salary-match`, {
                allocations, source_ledger_id: line.bank_ledger_id,
            });
            toast.success("Matched — journal posted");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Salary reconciliation"
                sub="Bank debit lines matched to outstanding Salary Payables. Handles single pay, partial, and prorated pooled."
                right={
                    <Button variant="outline" onClick={refresh} disabled={loading} data-testid="sr-refresh">
                        <ArrowsClockwise size={14} className="mr-2" /> Refresh
                    </Button>
                }
            />

            <div className="p-8 space-y-4">
                {items.length === 0 ? (
                    <div className="bg-white border rounded-md p-10 text-center text-stone-500" style={{ borderColor: "var(--se-border)" }}>
                        {loading ? "Scanning bank lines…" : "No unmatched bank debits with salary suggestions. All caught up 🎉"}
                    </div>
                ) : items.map((it) => (
                    <div key={it.line.id} className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={`sr-item-${it.line.id}`}>
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <div className="font-medium">₹ {inr(it.line.debit)}</div>
                                <div className="text-[11px] text-stone-500">{formatDate(it.line.date)} · {it.line.narration || "—"}</div>
                            </div>
                            <span className="se-chip text-[10px]" style={{ background: "#fef3c7", color: "#92400e" }}>
                                confidence {it.top_match.score}
                            </span>
                        </div>
                        <div className="p-5 space-y-3">
                            <SuggestionCard suggestion={it.top_match} isTop
                                onConfirm={() => applyMatch(it.line, it.top_match.allocations)}
                                onEdit={() => setEditing({ line: it.line, suggestion: it.top_match })} />
                            {it.alternates.length > 0 ? (
                                <details>
                                    <summary className="text-xs text-stone-500 cursor-pointer">Other suggestions ({it.alternates.length})</summary>
                                    <div className="mt-2 space-y-2">
                                        {it.alternates.map((s, i) => (
                                            <SuggestionCard key={i} suggestion={s}
                                                onConfirm={() => applyMatch(it.line, s.allocations)}
                                                onEdit={() => setEditing({ line: it.line, suggestion: s })} />
                                        ))}
                                    </div>
                                </details>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>

            <EditAllocationsDialog editing={editing} onClose={() => setEditing(null)} onDone={refresh} applyMatch={applyMatch} />
        </>
    );
}

function SuggestionCard({ suggestion, isTop, onConfirm, onEdit }) {
    const s = suggestion;
    return (
        <div className="border rounded-sm p-3" style={{
            borderColor: isTop ? "#16a34a" : "var(--se-border)",
            background: isTop ? "rgba(22, 163, 74, 0.05)" : "#fff",
        }}>
            <div className="flex items-start gap-2">
                {isTop ? <Sparkle size={14} className="mt-1 text-emerald-700" weight="fill" /> : null}
                <div className="flex-1">
                    <div className="text-[11px] text-stone-500 mb-1">{s.why}</div>
                    <table className="w-full text-xs">
                        <tbody>
                            {s.allocations.map((a, i) => (
                                <tr key={i}>
                                    <td className="py-1">{a.employee_name}</td>
                                    <td className="py-1 text-right text-stone-500">outstanding ₹{inr(a.outstanding_before)}</td>
                                    <td className="py-1 text-right font-medium">→ ₹ {inr(a.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex flex-col gap-2">
                    <Button size="sm" onClick={onConfirm} style={{ background: "#16a34a", color: "#fff" }} data-testid="sr-confirm">
                        <CheckCircle size={13} className="mr-1" /> Confirm
                    </Button>
                    <Button size="sm" variant="outline" onClick={onEdit} data-testid="sr-edit">
                        <PencilSimple size={13} className="mr-1" /> Adjust
                    </Button>
                </div>
            </div>
        </div>
    );
}

function EditAllocationsDialog({ editing, onClose, onDone, applyMatch }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (editing) setRows(editing.suggestion.allocations.map((a) => ({ ...a })));
    }, [editing]);
    if (!editing) return null;
    const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const debit = editing.line.debit;
    const balanced = Math.abs(total - debit) < 0.5;
    const submit = async () => {
        if (!balanced) return;
        const allocations = rows.filter((r) => Number(r.amount) > 0)
            .map((r) => ({ payroll_run_id: r.payroll_run_id, amount: Number(r.amount),
                            employee_name: r.employee_name, employee_id: r.employee_id }));
        await applyMatch(editing.line, allocations);
        onClose();
        onDone && onDone();
    };
    return (
        <Dialog open={!!editing} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="sr-edit-dialog">
                <DialogHeader><DialogTitle>Adjust allocation · ₹{inr(debit)}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-[11px] text-stone-500">
                        Enter the exact amount to allocate to each employee. Sum must equal the bank debit.
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] text-stone-500 uppercase">
                            <tr><th className="text-left">Employee</th><th className="text-right">Outstanding</th><th className="text-right">Allocate ₹</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.payroll_run_id}>
                                    <td className="py-2">{r.employee_name}</td>
                                    <td className="py-2 text-right text-stone-500">₹ {inr(r.outstanding_before)}</td>
                                    <td className="py-2 text-right w-32">
                                        <Input type="number" value={r.amount}
                                               onChange={(e) => {
                                                   const v = e.target.value;
                                                   setRows((prev) => prev.map((x, j) => j === i ? { ...x, amount: v } : x));
                                               }} className="text-right" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t" style={{ borderColor: "var(--se-border)" }}>
                                <td colSpan="2" className="py-2 text-right text-xs text-stone-500">Sum</td>
                                <td className="py-2 text-right font-medium" style={{ color: balanced ? "#16a34a" : "#dc2626" }}>
                                    ₹ {inr(total)}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="2" className="py-2 text-right text-xs text-stone-500">Bank debit</td>
                                <td className="py-2 text-right">₹ {inr(debit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={!balanced} style={{ background: "#16a34a", color: "#fff" }} data-testid="sr-edit-confirm">
                        <CheckCircle size={13} className="mr-1" /> Apply match
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
